/**
 * "Tilbúið án nets" (ready offline): only claimed when every piece is in place —
 * the service worker controls the page, the app shell is precached, the core queries are persisted
 * to IndexedDB, and at least one offline pack is complete.
 */
import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { readPersistedClient } from '../api/queryClient';

export interface Readiness {
  swActive: boolean;
  shellCached: boolean;
  corePersisted: boolean;
  packReady: boolean;
}

export const CORE_QUERY_ROOTS = ['me', 'members', 'songs', 'collections', 'events'] as const;

/** Do the persisted query roots (from the persister's JSON) include every core query? */
export function hasCoreQueries(roots: Iterable<unknown>): boolean {
  const have = new Set(roots);
  return CORE_QUERY_ROOTS.every((r) => have.has(r));
}

/**
 * Roots of the persisted queries that hold data — the same rule as shouldPersistQuery (api/queryClient):
 * a query whose last refetch failed offline keeps its data (status 'error', e.g. ['me'] after an
 * offline start) and is restored with it, so it counts.
 */
export function persistedRoots(client: Pick<PersistedClient, 'clientState'> | undefined): unknown[] {
  return (client?.clientState?.queries ?? [])
    .filter((q) => q.state?.data !== undefined && q.state?.data !== null)
    .map((q) => q.queryKey?.[0]);
}

export async function checkReadiness(packReady: boolean): Promise<Readiness> {
  const swActive = typeof navigator !== 'undefined' && 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
  let shellCached = false;
  try {
    shellCached = typeof caches !== 'undefined' && !!(await caches.match('/index.html', { ignoreSearch: true }));
  } catch {
    /* Cache API unavailable */
  }
  let corePersisted = false;
  try {
    // Only a payload this device would restore (it belongs to the last signed-in user) counts.
    corePersisted = hasCoreQueries(persistedRoots(await readPersistedClient()));
  } catch {
    /* IndexedDB unavailable / not persisted yet */
  }
  return { swActive, shellCached, corePersisted, packReady };
}

export function isReady(r: Readiness): boolean {
  return r.swActive && r.shellCached && r.corePersisted && r.packReady;
}
